import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
	linkedSignal,
	OnInit,
	output,
	untracked,
} from '@angular/core';
import { CourseSearchPayloadValue } from '../../../models/course-search-payload.models';
import { CourseSearchAutosuggestService } from '@uc/web/shared/feature/feature-course-search-autosuggest';
import {
	CLEARING_DEGREE_LEVELS,
	CLEARING_STUDY_OPTIONS,
	COURSE_SEARCH_FILTER_TOOLTIPS,
	LOCATIONS,
	POSTGRADUATE_DEGREE_LEVELS,
	STUDY_OPTIONS,
	UNDERGRADUATE_DEGREE_LEVELS,
} from '../../../static/courses-filter.properties';
import { FILTER_CLEARING_TOGGLE_DESC } from '../../../static/clearing-course-search.properties';
import { CourseSearchLocationGAEnum } from '@uc/web/shared/data-models';
import { Store } from '@ngrx/store';
import { getStateCourseSearchFilterToBeRemoved } from '../../../data-access/+store/course-search.selectors';
import { CourseSearchFilterStateService } from '../../../services/course-search-filter-state.service';
import { Router } from '@angular/router';
import { HECoSSubjectValidatorService } from '@uc/web/core';
import {
	queryParamsToObject,
	sortQueryParamsByKey,
} from '../../../utils/query-params.utils';
import { MAX_UCAS_TARIFF } from '@uc/web/shared/data-access';
import { CourseSearchFilterPill } from '../../../models/course-search-filter.models';
import { generateUcasPointsPill, generatePill } from '../../../utils/pill.utils';
import { isPostgraduate } from '../../../utils/utils';
import { updateFilterPills } from '../../../data-access/+store/course-search.actions';
import { determineSegmentsOnReset } from '../../../utils/route.utils';
import { Product, ProductEnum } from '@uc/shared/data-models';

@Component({
	selector: 'uc-course-filter-base',
	template: '',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourseFilterBaseComponent implements OnInit {
	courseSearchFilterStateSrv = inject(CourseSearchFilterStateService);
	protected store = inject(Store);
	protected router = inject(Router);
	protected hecosSubjectValidatorSrv = inject(HECoSSubjectValidatorService);
	private _autosuggestSrv = inject(CourseSearchAutosuggestService);

	isClearingSearch = input(false);
	showLocation = input(true);
	showDegreeLevel = input(true);
	isUniCourseSearch = input(false);
	degree = input<string>();
	location = input<string>();
	subject = input<string>();
	studyMode = input<string>();
	ucasPoints = input([undefined, undefined], {
		transform: (value: string | undefined) =>
			value ? value.split('_').map(Number) : [undefined, undefined],
	});
	uniSlug = input<string>();
	disableError = input(false);

	studyOptions = linkedSignal(() => {
		return structuredClone(
			this.isClearingSearch() ? CLEARING_STUDY_OPTIONS : STUDY_OPTIONS,
		);
	});

	studyLevel = linkedSignal<Product>(() => {
		const isCle = this.isClearingSearch();
		const isPG = isPostgraduate(this.degree());

		const studyLevel = isCle
			? ProductEnum.Clearing
			: isPG
				? ProductEnum.Postgraduate
				: ProductEnum.Undergraduate;
		return studyLevel;
	});

	degrees = linkedSignal(() => {
		const level = this.studyLevel();
		const degrees =
			level == ProductEnum.Clearing
				? CLEARING_DEGREE_LEVELS
				: level == ProductEnum.Postgraduate
					? POSTGRADUATE_DEGREE_LEVELS
					: UNDERGRADUATE_DEGREE_LEVELS;
		return structuredClone(degrees);
	});

	filterChange = output<CourseSearchPayloadValue>();
	filterReset = output<void>();
	autosuggest = output<{ searchTerm: string; degree?: string }>();
	closeFilter = output<boolean>();

	autoSuggestOpen = computed(() => this._autosuggestSrv.autoSuggestOpen());

	filterPills = computed(() => {
		const pills = this._generateFilterPills();
		untracked(() => {
			this.store.dispatch(updateFilterPills({ pills }));
		});
		return pills;
	});

	locations = structuredClone(LOCATIONS);
	tooltips = structuredClone(COURSE_SEARCH_FILTER_TOOLTIPS);

	readonly filterToggleDesc = FILTER_CLEARING_TOGGLE_DESC;
	readonly courseSearchFilterSearchLocation =
		CourseSearchLocationGAEnum.CourseSearchFilter;

	filterToBeRemoved = this.store.selectSignal(getStateCourseSearchFilterToBeRemoved);

	ngOnInit(): void {
		this._initialiseStudyOption();
		this._initialiseDegree();
		this._initialiseLocation();
	}

	onFilterState(filterName: string) {
		const _filterName = filterName as keyof CourseSearchFilterStateService;
		const filter = this.courseSearchFilterStateSrv[_filterName];
		if (filter) {
			this.courseSearchFilterStateSrv[_filterName] =
				filter === 'open' ? 'closed' : 'open';
		}
	}

	onUcasPointsSelect(ucasPoints: string) {
		if (!ucasPoints) return;

		const [min = 0, max = MAX_UCAS_TARIFF] = ucasPoints.split('_');

		this.filterChange.emit({ tariffMin: Number(min), tariffMax: Number(max) });
		this.updateQueryParams(`ucaspts=${ucasPoints}`);
	}

	protected resetLocation() {
		const locations = this.locations.map((location) => ({
			...location,
			checked: false,
		}));
		locations[0].checked = true;
		this.locations = structuredClone(locations);
	}

	protected resetStudyOption() {
		const options = this.studyOptions().map((option) => ({
			...option,
			checked: false,
		}));
		this.studyOptions.set(structuredClone(options));
	}

	protected resetDegree() {
		const degrees = structuredClone(this.degrees());
		degrees.forEach((degree) => (degree.checked = false));
		degrees[0].checked = true;
		this.degrees.set(degrees);
	}

	protected updateQueryParams(link: string) {
		const queryParams = this.router.url.split('?')[1];
		const queryParamsObject = queryParamsToObject(`${queryParams}&${link}`);

		if (queryParamsObject) {
			const sortedQueryParams = sortQueryParamsByKey(queryParamsObject);
			this.router.navigate([], {
				queryParams: { ...sortedQueryParams },
			});
		}
	}

	protected determineSegmentsOnFilterReset(isHECoSSubject: boolean, subject: string) {
		return determineSegmentsOnReset(
			this.studyLevel(),
			this.isUniCourseSearch(),
			isHECoSSubject,
			subject,
		);
	}

	private _generateFilterPills() {
		const pills = [
			generateUcasPointsPill(this.ucasPoints()),
			generatePill(this.degree(), this.degrees(), 'DEGREE'),
			generatePill(this.location(), this.locations, 'LOCATION'),
			generatePill(`mode=${this.studyMode()}`, this.studyOptions(), 'STUDY_MODE'),
		];

		return pills.filter(Boolean) as CourseSearchFilterPill[];
	}

	private _initialiseDegree() {
		this.degrees().forEach(
			(degree) => (degree.checked = degree.link === this.degree()),
		);
	}

	private _initialiseStudyOption() {
		const mode = this.studyMode();
		if (!mode) return;
		this.studyOptions().forEach(
			(option) => (option.checked = option.link.includes(mode)),
		);
	}

	private _initialiseLocation(): void {
		this.locations.forEach((location) => {
			const isNoLocationProvided = !this.location() && location.link === '';
			if (isNoLocationProvided) {
				location.checked = true;
			} else {
				location.checked = location.link === this.location();
			}
		});
	}
}
